import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Home
          </Link>
          <h1 className="text-3xl font-bold text-slate-900 mt-4">Privacy Policy</h1>
          <p className="text-slate-600 mt-2">Last updated: October 6, 2025</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Introduction</h2>
            <p className="text-slate-600 leading-relaxed">
              RetailForge ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use StockSentry, our Shopify inventory management application.
            </p>
          </section>

          {/* Data Collection */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data We Collect</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We collect only the minimum data necessary to provide inventory alert services:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong className="text-slate-900">Product Data:</strong> Inventory levels, SKUs, product names</li>
              <li><strong className="text-slate-900">Sales Data:</strong> Historical sales velocity for forecasting</li>
              <li><strong className="text-slate-900">Store Configuration:</strong> Alert preferences, notification settings</li>
              <li><strong className="text-slate-900">Contact Information:</strong> Email address, phone number (if SMS enabled)</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong className="text-slate-900">We do not collect:</strong> Customer names, addresses, payment information, or any personally identifiable customer information beyond what's needed for notifications.
            </p>
          </section>

          {/* Data Usage */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">How We Use Your Data</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Provide inventory forecasting and low stock alerts</li>
              <li>Analyze sales velocity patterns and seasonal trends</li>
              <li>Send notifications via email, SMS, or Slack</li>
              <li>Improve our services and develop new features</li>
              <li>Comply with legal obligations and enforce our terms</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Security</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We implement industry-standard security measures in compliance with Shopify's protected customer data requirements:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong className="text-slate-900">Encryption:</strong> All data encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
              <li><strong className="text-slate-900">Access Controls:</strong> Role-based access with multi-factor authentication</li>
              <li><strong className="text-slate-900">Environment Separation:</strong> Production and test environments completely isolated</li>
              <li><strong className="text-slate-900">Monitoring:</strong> Continuous security monitoring with automated threat detection</li>
              <li><strong className="text-slate-900">Audit Logs:</strong> Comprehensive access logs for security reviews</li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Sharing</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              <strong className="text-slate-900">We never sell your data.</strong> Limited sharing only occurs when necessary:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong className="text-slate-900">Notification Services:</strong> Email/SMS providers (only contact info shared)</li>
              <li><strong className="text-slate-900">Cloud Infrastructure:</strong> Secure hosting providers (AWS/GCP) with encryption</li>
              <li><strong className="text-slate-900">Analytics:</strong> Anonymized usage data for service improvement</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              All third-party providers are vetted, GDPR-compliant, and bound by strict data processing agreements.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Data Retention</h2>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong className="text-slate-900">Active Accounts:</strong> Sales velocity data retained up to 24 months for seasonal analysis</li>
              <li><strong className="text-slate-900">Uninstallation:</strong> All data deleted within 30 days of app removal</li>
              <li><strong className="text-slate-900">Account Deletion:</strong> Complete data erasure within 48 hours upon request</li>
              <li><strong className="text-slate-900">Backups:</strong> Encrypted backups retained 90 days, then permanently deleted</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Your Privacy Rights</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We comply with GDPR, CCPA/CPRA, PIPEDA, and Shopify's protected customer data policies. You have the right to:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li><strong className="text-slate-900">Access:</strong> Request a copy of your data</li>
              <li><strong className="text-slate-900">Rectification:</strong> Correct inaccurate data</li>
              <li><strong className="text-slate-900">Erasure:</strong> Request deletion of your data</li>
              <li><strong className="text-slate-900">Portability:</strong> Export your data in a machine-readable format</li>
              <li><strong className="text-slate-900">Opt-Out:</strong> Unsubscribe from marketing communications</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              To exercise your rights, contact us at{' '}
              <a href="mailto:support@retailforge.app" className="text-blue-600 hover:text-blue-700 font-medium underline">
                support@retailforge.app
              </a>
            </p>
          </section>

          {/* Security Incidents */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Security Incident Response</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              In the event of a security incident:
            </p>
            <ul className="list-disc list-inside text-slate-600 space-y-2 ml-4">
              <li>Automated detection and containment within minutes</li>
              <li>Affected merchants notified within 72 hours (GDPR requirement)</li>
              <li>Full forensic analysis to determine scope and impact</li>
              <li>Security vulnerabilities patched immediately</li>
              <li>Regulatory authorities notified as required by law</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              Report security concerns to{' '}
              <a href="mailto:support@retailforge.app" className="text-blue-600 hover:text-blue-700 font-medium underline">
                support@retailforge.app
              </a>
            </p>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Children's Privacy</h2>
            <p className="text-slate-600 leading-relaxed">
              StockSentry is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children.
            </p>
          </section>

          {/* Policy Updates */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Policy Updates</h2>
            <p className="text-slate-600 leading-relaxed">
              We may update this Privacy Policy periodically. Significant changes will be communicated via email. Continued use of StockSentry after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              For privacy-related questions or concerns:
            </p>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong className="text-slate-900">Email:</strong>{' '}
              <a href="mailto:support@retailforge.app" className="text-blue-600 hover:text-blue-700 font-medium underline">
                support@retailforge.app
              </a>
            </p>
            <p className="text-slate-600 leading-relaxed mt-2">
              <strong className="text-slate-900">Company:</strong> RetailForge
            </p>
          </section>
        </div>

        {/* Back to Home */}
        <div className="mt-8 text-center">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-medium">
            ← Back to Home
          </Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
